import React, { useEffect, useState, useContext } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { buildEventOpenGraph } from '@blocktickets/shared-utils'

import LoadingContext from '../../context/Loading/Loading'

import { getEventByShortCode, getTaxRates } from '../../utilities/api'

import {
  NewSeatedPurchaseWrapper,
  PageLoadingContainer,
} from '../../components'
import NotFoundPage from '../NotFoundPage/NotFoundPage'
import OpenGraph from '../../components/OpenGraph/OpenGraph'
import useFiltersStore from '../../stores/filtersStore'

import { initializeTracking, trackViewItem } from '../../utilities/tracking'
import { rememberTrackingCode } from '../../utilities/trackingLink'
import { cacheEventBranding } from '../../utilities/orgBrandingCache'
import useWaitingRoomHeartbeat from '../../hooks/useWaitingRoomHeartbeat'
import {
  hasValidWaitingRoomToken,
  isWaitingRoomRequired,
  redirectToWaitingRoom,
} from '../../utilities/waitingRoom'

export default function NewSeatedPurchasePage() {
  // Zustand
  const setEventTicketLimit = useFiltersStore(
    (state) => state.setEventTicketLimit
  )
  const setEvent = useFiltersStore((state) => state.setEvent)
  const setCode = useFiltersStore((state) => state.setCode)
  const event = useFiltersStore((state) => state.event)

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  let { shortcode, slug } = useParams()
  const [hasError, setHasError] = useState(false)

  useWaitingRoomHeartbeat(
    event?.uuid,
    isWaitingRoomRequired(event) && hasValidWaitingRoomToken(event?.uuid)
  )

  useEffect(() => {
    showLoading()
    const code = searchParams.get('code') ? searchParams.get('code') : 0
    setCode(code)
    getEventByShortCode(shortcode, slug, code)
      .then((res) => {
        const organization = res.data?.event?.organization

        initializeTracking(organization)
        cacheEventBranding(res.data?.event, organization)

        if (res.data?.status === 404) {
          hideLoading()
          setHasError(true)
          return
        }
        setHasError(false)
        const loadedEvent = res.data?.event
        rememberTrackingCode(loadedEvent?.uuid, code)
        if (
          isWaitingRoomRequired(loadedEvent) &&
          !hasValidWaitingRoomToken(loadedEvent.uuid)
        ) {
          hideLoading()
          redirectToWaitingRoom(
            navigate,
            slug,
            shortcode,
            loadedEvent.uuid,
            'seated'
          )
          return
        }
        setEvent(loadedEvent)
        setEventTicketLimit(loadedEvent.globalTicketLimit)
        trackViewItem({
          organization,
          event: res.data?.event,
        })
        setTimeout(() => hideLoading(), 700)
      })
      .catch((err) => {
        console.error(err)
        hideLoading()
      })
  }, [shortcode]) // load event whenever event code changes

  const openGraph = buildEventOpenGraph(event)

  return (
    <>
      <OpenGraph {...openGraph} />
      {event && (
        <Helmet>
          <meta charSet="utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1"
          />
          <meta name="theme-color" content="#000000" />
          <meta name="keywords" content={openGraph.description} />
        </Helmet>
      )}
      {isLoading ? (
        <PageLoadingContainer organization={event?.organization} />
      ) : (
        <>
          {event && !hasError && <NewSeatedPurchaseWrapper />}

          {hasError && <NotFoundPage />}
        </>
      )}
    </>
  )
}
